import { fetchApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export type ContractStatus = 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'PENDING';
export type BillingCycle = 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';
export type EmployeeStatus = 'ACTIVE' | 'INACTIVE';

export interface CorporateContract {
  id: string;
  clinicId: string;
  companyName: string;
  companyGst?: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  contractValue: number;
  employeeCount: number;
  servicesIncluded?: string;
  consultationDiscountPct: number;
  labDiscountPct: number;
  billingCycle: BillingCycle;
  status: ContractStatus;
  contractStart: string;
  contractEnd: string;
  createdAt: string;
  updatedAt: string;
}

export interface CorporateEmployee {
  id: string;
  clinicId: string;
  contractId: string;
  patientId?: string;
  employeeId: string;
  employeeName: string;
  employeeEmail?: string;
  department?: string;
  status: EmployeeStatus;
  enrolledAt: string;
}

export interface CorporateSummary {
  activeContracts: number;
  totalCoveredEmployees: number;
  totalContractValue: number;
  totalEmployees: number;
}

export const corporateModule = {
  listContracts: (activeOnly = true) =>
    fetchApi<CorporateContract[]>(`/api/corporate/contracts?activeOnly=${activeOnly}`),

  createContract: (contract: Omit<CorporateContract, 'id' | 'clinicId' | 'createdAt' | 'updatedAt'>) =>
    fetchApi<CorporateContract>('/api/corporate/contracts', {
      method: 'POST',
      body: JSON.stringify(contract),
    }),

  updateContract: (id: string, contract: Partial<CorporateContract>) =>
    fetchApi<CorporateContract>(`/api/corporate/contracts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(contract),
    }),

  cancelContract: (id: string) =>
    fetchApi<{ message: string }>(`/api/corporate/contracts/${id}`, { method: 'DELETE' }),

  getEmployees: (contractId: string) =>
    fetchApi<CorporateEmployee[]>(`/api/corporate/contracts/${contractId}/employees`),

  /** One page of a contract's employees, name order, searched on the server (medopd-docs 112). */
  getEmployeesPage: (contractId: string, params: { page: number; size: number; search?: string }) => {
    const p = new URLSearchParams({ page: String(params.page), size: String(params.size) });
    if (params.search) p.set('search', params.search);
    return fetchApi<PagedResponse<CorporateEmployee>>(`/api/corporate/contracts/${contractId}/employees?${p.toString()}`);
  },

  addEmployee: (contractId: string, employee: Omit<CorporateEmployee, 'id' | 'clinicId' | 'contractId' | 'enrolledAt'>) =>
    fetchApi<CorporateEmployee>(`/api/corporate/contracts/${contractId}/employees`, {
      method: 'POST',
      body: JSON.stringify(employee),
    }),

  bulkAddEmployees: (contractId: string, employees: Omit<CorporateEmployee, 'id' | 'clinicId' | 'contractId' | 'enrolledAt'>[]) =>
    fetchApi<CorporateEmployee[]>(`/api/corporate/contracts/${contractId}/employees/bulk`, {
      method: 'POST',
      body: JSON.stringify(employees),
    }),

  deactivateEmployee: (contractId: string, employeeId: string) =>
    fetchApi<{ message: string }>(`/api/corporate/contracts/${contractId}/employees/${employeeId}`, {
      method: 'DELETE',
    }),

  getSummary: () =>
    fetchApi<CorporateSummary>('/api/corporate/summary'),
};
